import {
  type CreateOrganizationRequest,
  MfaPolicy,
  OrganizationStatus,
} from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { localeList, timezoneList, z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormSection } from '../../../components/forms/FormSection';
import { HookedFormField } from '../../../components/forms/HookedFormField';
import { HookedImageUploader } from '../../../components/forms/HookedImageUploader';
import { HookedInput } from '../../../components/forms/HookedInput';
import { HookedResetButton } from '../../../components/forms/HookedResetButton';
import { HookedSelect } from '../../../components/forms/HookedSelect';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { ContentLayout } from '../../../components/layouts/ContentLayout';
import { useCreateOrganizationMutation } from '../../../graphql/hooks/useCreateOrganizationMutation';
import { useAppContext } from '../../../hooks/useAppContext';
import { zodResolver } from '../../../utils/zod';

export const Onboard = () => {
  const { successToast, errorToast } = useAppContext();

  const mutation = useCreateOrganizationMutation();

  const initialValues: CreateOrganizationRequest = {
    name: '',
    status: OrganizationStatus.Trial,
    logo: null,
    timezone: 'Australia/Brisbane',
    locale: 'en-au',
    supportEmail: 'dom@piccolohealth.com',
    mfaPolicy: MfaPolicy.Optional,
    stripeCustomerId: null,
    stripeSubscriptionId: null,
  };

  const validationSchema: z.ZodType<CreateOrganizationRequest> = z.object({
    name: z.string(),
    status: z.enum(Object.values(OrganizationStatus)),
    logo: z.string().nullish(),
    timezone: z.string(),
    locale: z.string(),
    supportEmail: z.string(),
    mfaPolicy: z.enum(Object.values(MfaPolicy)),
    stripeCustomerId: z.string().nullable(),
    stripeSubscriptionId: z.string().nullable(),
  });

  const methods = useForm<CreateOrganizationRequest>({
    defaultValues: initialValues,
    resolver: zodResolver(validationSchema),
  });

  const onSubmit = async (values: CreateOrganizationRequest) => {
    await mutation
      .mutateAsync({
        createOrganizationRequest: values,
      })
      .then(() => {
        successToast('Organization created successfully');
      })
      .catch((err) => {
        errorToast(`Error creating organization: ${err.message}`);
      });
  };

  const statusOptions = Object.keys(OrganizationStatus).map((key) => {
    return {
      key,
      value: OrganizationStatus[key as keyof typeof OrganizationStatus],
      label: key,
      raw: OrganizationStatus[key as keyof typeof OrganizationStatus],
    };
  });

  const timezoneOptions = timezoneList.map((timezone) => ({
    value: timezone,
    label: timezone,
    raw: timezone,
  }));

  const localeOptions = localeList.map((locale) => ({
    key: locale,
    value: locale,
    label: locale,
    raw: locale,
  }));

  const mfaPolicyOptions = Object.keys(MfaPolicy).map((key) => ({
    key,
    value: MfaPolicy[key as keyof typeof MfaPolicy],
    label: key,
    raw: MfaPolicy[key as keyof typeof MfaPolicy],
  }));

  return (
    <ContentLayout>
      <ChakraV3.Stack maxW='400px' px={6}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <FormSection heading='Create an organization'>
              <HookedFormField label='Name' name='name'>
                <HookedInput name='name' placeholder='Company name' />
              </HookedFormField>
              <HookedFormField label='Status' name='status'>
                <HookedSelect name='status' options={statusOptions} />
              </HookedFormField>
              <HookedFormField label='Logo' name='logo'>
                <HookedImageUploader name='logo' maxWidth={160} maxHeight={120} />
              </HookedFormField>
              <HookedFormField label='Timezone' name='timezone'>
                <HookedSelect name='timezone' options={timezoneOptions} />
              </HookedFormField>
              <HookedFormField label='Locale' name='locale'>
                <HookedSelect name='locale' options={localeOptions} />
              </HookedFormField>
              <HookedFormField label='MFA Policy' name='mfaPolicy'>
                <HookedSelect name='mfaPolicy' options={mfaPolicyOptions} />
              </HookedFormField>
              <HookedFormField label='Support Email' name='supportEmail'>
                <HookedInput name='supportEmail' placeholder='Support Email' />
              </HookedFormField>
              <HookedFormField label='Stripe Customer ID' name='stripeCustomerId'>
                <HookedInput name='stripeCustomerId' placeholder='Stripe Customer ID' />
              </HookedFormField>
              <HookedFormField label='Stripe Subscription ID' name='stripeSubscriptionId'>
                <HookedInput name='stripeSubscriptionId' placeholder='Stripe Subscription ID' />
              </HookedFormField>
            </FormSection>
            <ChakraV3.Separator my={3} />
            <ChakraV3.HStack alignItems='center'>
              <HookedResetButton>Reset</HookedResetButton>
              <HookedSubmitButton>Save</HookedSubmitButton>
            </ChakraV3.HStack>
          </form>
        </FormProvider>
      </ChakraV3.Stack>
    </ContentLayout>
  );
};
